import { Clock, Send } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { AnimatePresence, motion, type Transition } from "motion/react";

import { PunchupButton } from "../../common/PunchupButton";
import { Stepper } from "../../common/Stepper";
import { SECTION_META } from "../constants";
import { AccordionSectionId } from "../types";
import { MobileFooterConfig } from "../scheduler/SmartScheduler";
import { useSwipeNavigation } from "./hooks/useSwipeNavigation";
import { slideVariants, SLIDE_TRANSITION } from "../../../animations/slide";

/** Concise step titles for the mobile stepper */
export const STEP_LABELS: Record<AccordionSectionId, string> = {
  "use-case": "Type",
  audience: "Audience",
  "channel-preference": "Channel",
  composition: "Compose",
  review: "Review",
};

export interface MobileStepperFlowProps {
  activeSection: AccordionSectionId | null;
  sectionOrder: AccordionSectionId[];
  completedSections: Set<AccordionSectionId>;
  channelPreferenceSkipped: boolean;
  onSectionChange: (sectionId: AccordionSectionId) => void;
  renderSectionContent: (sectionId: AccordionSectionId) => React.ReactNode;
  mobileConfirmConfig: {
    onClick: () => void;
    disabled: boolean;
    disabledMessage?: string;
  } | null;
  reviewFooterConfig: MobileFooterConfig | null;
  /** Optional element rendered on the RIGHT side of the header bar (e.g. a close X button) */
  headerAction?: React.ReactNode;
  /** Optional element rendered on the LEFT side of the header bar, before the title (e.g. a back arrow) */
  headerBackAction?: React.ReactNode;
  /** When provided, renders venue context and overrides the header subtitle */
  venueContext?: { name: string; subtitle: string };
  /** Sections to exclude from the stepper dots (in addition to channelPreferenceSkipped) */
  hiddenSections?: AccordionSectionId[];
}

export function MobileStepperFlow({
  activeSection,
  sectionOrder,
  completedSections,
  channelPreferenceSkipped,
  onSectionChange,
  renderSectionContent,
  mobileConfirmConfig,
  reviewFooterConfig,
  headerAction,
  headerBackAction,
  venueContext,
  hiddenSections,
}: MobileStepperFlowProps) {
  const drawerSections = useMemo(
    () =>
      sectionOrder.filter(
        (s) =>
          (s !== "channel-preference" || !channelPreferenceSkipped) &&
          !(hiddenSections ?? []).includes(s)
      ),
    [sectionOrder, channelPreferenceSkipped, hiddenSections]
  );

  // When activeSection is hidden (e.g. audience auto-completing during skip),
  // display the first visible section's header while the hidden section runs.
  const displaySection = useMemo(() => {
    if (activeSection && drawerSections.includes(activeSection)) {
      return activeSection;
    }
    return drawerSections[0] ?? null;
  }, [activeSection, drawerSections]);

  const slideDirection = useRef(1);

  const navigateDrawer = useCallback(
    (direction: "back" | "forward") => {
      if (!displaySection) return;
      const currentIdx = drawerSections.indexOf(displaySection);
      if (currentIdx === -1) return;

      if (direction === "back") {
        const prevSection = drawerSections[currentIdx - 1];
        if (!prevSection) return;
        slideDirection.current = -1;
        onSectionChange(prevSection);
      } else {
        const nextSection = drawerSections[currentIdx + 1];
        const allPriorComplete = drawerSections
          .slice(0, currentIdx + 1)
          .every((s) => completedSections.has(s));
        if (!nextSection || !allPriorComplete) return;
        slideDirection.current = 1;
        onSectionChange(nextSection);
      }
    },
    [displaySection, drawerSections, onSectionChange, completedSections]
  );

  const { onTouchStart, onTouchEnd } = useSwipeNavigation({
    onSwipeLeft: () => navigateDrawer("back"),
    onSwipeRight: () => navigateDrawer("forward"),
  });

  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  const effectiveTransition: Transition = prefersReducedMotion
    ? { duration: 0 }
    : SLIDE_TRANSITION;

  // While a hidden section is actively loading (e.g. audience auto-applying),
  // suppress all visual chrome so there's no flash of intermediate content.
  const isAutoSkipping =
    hiddenSections &&
    hiddenSections.length > 0 &&
    activeSection != null &&
    !drawerSections.includes(activeSection);

  return (
    <>
      {/* Header with section title */}
      {displaySection && (
        <div
          className={`flex items-start justify-between gap-2 border-b border-neutral-200 px-4 pb-3 ${headerBackAction || headerAction ? "pt-2" : "pt-0"}`}
        >
          {headerBackAction}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-primary-blue sm:text-lg">
              {SECTION_META[displaySection].title}
            </h2>
            <p className="truncate text-xs font-normal text-secondary-blue sm:text-sm">
              {venueContext
                ? venueContext.subtitle
                : SECTION_META[displaySection].desc}
            </p>
          </div>
          {headerAction}
        </div>
      )}

      {/* While hidden sections auto-complete, show loading instead of stepper/content */}
      {isAutoSkipping ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-blue border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Stepper */}
          <Stepper
            steps={drawerSections.map((id) => ({
              label: STEP_LABELS[id],
            }))}
            activeStepIndex={
              displaySection ? drawerSections.indexOf(displaySection) : 0
            }
            onStepClick={(stepIndex) => {
              const targetId = drawerSections[stepIndex];
              if (!targetId) return;
              const isCompleted = completedSections.has(targetId);
              const isReachable = drawerSections
                .slice(0, stepIndex)
                .every((s) => completedSections.has(s));
              if (!isCompleted && !isReachable && targetId !== displaySection)
                return;
              const currentIdx = drawerSections.indexOf(displaySection!);
              slideDirection.current = stepIndex >= currentIdx ? 1 : -1;
              onSectionChange(targetId);
            }}
            showLabels
            className="px-4 pt-2 pb-4"
          />

          {/* Scrollable content with slide animation */}
          <div
            className="flex-1 overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence
              mode="wait"
              custom={slideDirection.current}
              initial={false}
            >
              <motion.div
                key={activeSection}
                custom={slideDirection.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={effectiveTransition}
                className="h-full overflow-y-auto px-4 pb-6"
              >
                {activeSection && renderSectionContent(activeSection)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Hidden mount layer — inactive sections stay mounted so hooks/effects keep running */}
          {sectionOrder.map((sectionId) =>
            sectionId !== activeSection ? (
              <div key={sectionId} className="hidden" aria-hidden="true">
                {renderSectionContent(sectionId)}
              </div>
            ) : null
          )}

          {/* Sticky footer */}
          {mobileConfirmConfig ? (
            <div className="flex flex-col items-center border-t border-neutral-200 px-4 py-3">
              {mobileConfirmConfig.disabled &&
                mobileConfirmConfig.disabledMessage && (
                  <p className="mb-2 text-center text-sm text-[#787B7E]">
                    {mobileConfirmConfig.disabledMessage}
                  </p>
                )}
              <PunchupButton
                className="w-full"
                onClick={mobileConfirmConfig.onClick}
                disabled={mobileConfirmConfig.disabled}
              >
                Confirm
              </PunchupButton>
            </div>
          ) : displaySection === "review" && reviewFooterConfig ? (
            <div className="flex justify-center border-t border-neutral-200 px-4 py-3">
              <PunchupButton
                className="w-full gap-1.5 disabled:bg-[#ff5e26]/40"
                onClick={reviewFooterConfig.onClick}
                disabled={reviewFooterConfig.disabled}
              >
                {reviewFooterConfig.isImmediate ? (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    {reviewFooterConfig.isSubmitting
                      ? "Sending..."
                      : "Send Message"}
                  </>
                ) : (
                  <>
                    <Clock className="h-3.5 w-3.5" />
                    {reviewFooterConfig.isSubmitting
                      ? "Scheduling..."
                      : "Schedule Message"}
                  </>
                )}
              </PunchupButton>
            </div>
          ) : null}
        </>
      )}

      {/* Hidden sections still mount so their hooks/effects run (e.g. autoApply) */}
      {isAutoSkipping &&
        sectionOrder.map((sectionId) => (
          <div key={sectionId} className="hidden">
            {renderSectionContent(sectionId)}
          </div>
        ))}
    </>
  );
}
